import productionService from './productionService';
import apiClient from './apiClient';
import { trackPromise } from 'react-promise-tracker';
import { enqueue, getQueue, offlineGet } from './helpers/offlineHelpers';
import { formatLocalDateTime } from './operatorService';

function toLocalDateLabel(isoDateTime) {
    if (!isoDateTime) {
        return '';
    }
    const date = new Date(isoDateTime);
    return date.toLocaleDateString('es-MX');
}

function toLocalTimeLabel(isoDateTime) {
    if (!isoDateTime) {
        return '';
    }
    const date = new Date(isoDateTime);
    return date.toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
}

async function listMyProductionRecords(date) {
    const records = await trackPromise(productionService.listMyRecords(date));
    return records.map((record) => ({
        id: record.id,
        machineId: record.machineId,
        timestamp: record.timestampHour,
        date: toLocalDateLabel(record.timestampHour),
        time: toLocalTimeLabel(record.timestampHour),
        machine: record.machineIdentifier,
        goodPieces: record.goodCount ?? 0,
        defectivePieces: record.defectiveCount ?? 0,
        defectTypeId: firstDefined(
            record.defectTypeId,
            record.defect_type_id,
            record.reasonId,
            record.reason_id,
            record.downTimeTypeId,
            record.down_time_type_id,
            record.defectReasons?.[0]?.defectTypeId,
            record.defectReasons?.[0]?.defect_type_id,
            record.defectReasons?.[0]?.reasonId,
            record.defectReasons?.[0]?.reason_id
        ),
        shiftId: record.shiftId ?? null,
        status: 'Registrado',
        notes: record.notes || '',
        timeCicle: record.timeCicle,
        defectDetails: record.defectDetails
    }));
}

async function createEditRequest({
    request,
    newGoodPiecesQty,
    newDefectivePiecesQty,
    recalculatedDefectiveQty,
    defectDetails,
    correctionTypeId,
    timeCicle
}) {
    const defectiveCount = newDefectivePiecesQty ?? recalculatedDefectiveQty;
    const hasDefectivePieces = parseIntSafe(defectiveCount) > 0;

    if (hasDefectivePieces && (!defectDetails || defectDetails.length === 0)) {
        throw new Error('Debes capturar al menos un tipo de defecto cuando la cantidad defectuosa es mayor a 0.');
    }

    const payload = {
        productionRecordId: request,
        goodCount: newGoodPiecesQty,
        defectiveCount: newDefectivePiecesQty,
        defectDetails: defectDetails,
        correctionTypeId: correctionTypeId,
        timeCicle: timeCicle,
        createdAt: formatLocalDateTime() // 🔥 importante
    };

    if (!navigator.onLine) {
        enqueue({
            type: 'CREATE_EDIT_REQUEST',
            payload
        });

        return {
            success: true,
            offline: true,
            message: 'Solicitud guardada sin conexión'
        };
    }

    return trackPromise(productionService.registerEditRequest(payload));
}

function asArray(value) {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.content)) return value.content;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.rows)) return value.rows;
    return [];
}

function parseIntSafe(value) {
    if (value === null || value === undefined || value === '') return '-';

    const number = Number(value);
    if (Number.isNaN(number)) return null;

    return parseInt(number, 10);
}

function normalizeDefectReasonList(sourceList) {
    return sourceList
        .map((item) => {
            if (!item) return null;

            if (Array.isArray(item) && item.length >= 2) {
                const [rawName, rawQuantity] = item;
                const quantity = parseIntSafe(rawQuantity);
                if (quantity <= 0) return null;
                return {
                    id: String(rawName),
                    name: String(rawName),
                    quantity
                };
            }

            const id =
                item?.reasonId ??
                item?.reason_id ??
                item?.defectTypeId ??
                item?.defect_type_id ??
                item?.downTimeTypeId ??
                item?.down_time_type_id ??
                item?.id ??
                item?.code ??
                item?.key;

            const quantity = parseIntSafe(item?.quantity ?? item?.qty ?? item?.pieces ?? item?.count ?? item?.value);
            const name =
                item?.name ??
                item?.reasonName ??
                item?.reason_name ??
                item?.defectName ??
                item?.defect_name ??
                item?.typeName ??
                item?.type_name ??
                item?.label ??
                item?.description ??
                '';

            return { id, name, quantity };
        })
        .filter((item) => item && item.id !== undefined && item.id !== null && String(item.id).trim() !== '' && item.quantity > 0)
        .map((item) => ({
            id: item.id,
            name: item.name || `#${item.id}`,
            quantity: item.quantity
        }));
}

function objectMapToDefectReasons(mapValue) {
    if (!mapValue || Array.isArray(mapValue) || typeof mapValue !== 'object') return [];
    return Object.entries(mapValue).map(([name, quantity]) => ({
        id: name,
        name,
        quantity: parseIntSafe(quantity)
    }));
}

function firstDefined(...values) {
    return values.find((value) => value !== undefined && value !== null);
}

function extractDefectReasons(record) {
    const nestedChanges = record?.changes ?? record?.requestedChanges ?? record?.editChanges;
    const sources = [
        record?.defectReasons,
        record?.defect_reasons,
        record?.defectDistribution,
        record?.requestedDefectReasons,
        record?.newDefectReasons,
        record?.defects,
        record?.reasons,
        nestedChanges?.defectReasons,
        nestedChanges?.defect_reasons,
        nestedChanges?.defects,
        nestedChanges?.distribution
    ];

    const arraySource = sources.find((entry) => Array.isArray(entry));
    if (arraySource) {
        return normalizeDefectReasonList(arraySource);
    }

    const mapSource = firstDefined(
        record?.defectReasonQuantities,
        record?.defect_reason_quantities,
        record?.defectsByType,
        record?.defects_by_type,
        nestedChanges?.defectReasonQuantities,
        nestedChanges?.defect_reason_quantities,
        nestedChanges?.defectsByType,
        nestedChanges?.defects_by_type
    );
    if (mapSource && typeof mapSource === 'object') {
        return normalizeDefectReasonList(objectMapToDefectReasons(mapSource));
    }

    return [];
}

function toDateLabel(value) {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString('es-MX');
}

function toTimeLabel(value) {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
}

function normalizePendingEditRequest(record) {
    const defectReasons = extractDefectReasons(record);
    //const nestedChanges = record?.changes ?? record?.requestedChanges ?? record?.editChanges;
    const requestedDefectiveFromReasons = defectReasons.reduce((acc, item) => acc + parseIntSafe(item.quantity), 0);
    const createdAt = firstDefined(record?.createdAt, record?.requestedAt);
    const timestamp = firstDefined(record?.timestampHour);
    const machine =
        firstDefined(
            record?.machineIdentifier,
            record?.machineCode,
            record?.machineName,
            record?.machine?.identifier,
            record?.machine?.code,
            record?.machine?.name
        ) ?? null;
    const machineId = firstDefined(record?.machineId, record?.machine?.id);
    const reportId = firstDefined(record?.productionRecordId, record?.production?.id);
    const motive = firstDefined(record?.reason, record?.motive) ?? '';

    const originalGoodPieces = parseIntSafe(record?.originalGoodCount);
    const originalDefectivePieces = parseIntSafe(record?.originalDefectiveCount);
    const newGoodPieces = parseIntSafe(record?.requestedGoodCount);
    const newDefectivePiecesRaw = parseIntSafe(record?.requestedDefectiveCount);
    const newDefectivePieces = newDefectivePiecesRaw;
    newDefectivePiecesRaw !== undefined && newDefectivePiecesRaw !== null
        ? parseIntSafe(newDefectivePiecesRaw)
        : requestedDefectiveFromReasons;

    return {
        id: record?.id ?? record?.requestId ?? record?.editRequestId,
        operator: record?.operatorName ?? record?.requestedByName ?? record?.createdByName ?? record?.operator?.fullName ?? 'Sin operador',
        reference: record?.reportReference ?? reportId ?? '-',
        reportId,
        machineId,
        machine: machine || (machineId ? `#${machineId}` : 'Sin máquina'),
        date: toDateLabel(new Date(timestamp)),
        time: toTimeLabel(new Date(timestamp)),
        dateCreatedAt: toDateLabel(new Date(createdAt)),
        timeCreatedAt: toTimeLabel(new Date(createdAt)),
        status: record?.status ?? 'PENDING',
        motive,
        originalGoodPieces,
        originalDefectivePieces,
        newGoodPieces,
        newDefectivePieces,
        defectReasons,
        hasDefectBreakdown: defectReasons.length > 0,
        raw: record,
        originalTimeCicle: record.originalTimeCicle,
        requestedTimeCicle: record.requestedTimeCicle,
        requestedDefectDetails: record.requestedDefectDetails,
        originalDefectDetails: record.originalDefectDetails
    };
}

async function tryRequestCandidates(candidates) {
    let lastError;
    for (const candidate of candidates) {
        try {
            return await candidate();
        } catch (error) {
            const status = error?.response?.status;
            if (status === 404 || status === 405) {
                lastError = error;
                continue;
            }
            throw error;
        }
    }
    throw lastError || new Error('No se encontró un endpoint válido para solicitudes de edición.');
}

async function listPendingEditRequests() {
    // const response = await tryRequestCandidates([() => trackPromise(apiClient.get('/api/edit-requests/pending'))]);
    //
    // const rows = asArray(response?.data?.data ?? response?.data);
    // return rows.map(normalizePendingEditRequest).filter((item) => item.id !== undefined && item.id !== null);
    const rows = await offlineGet(
        'pending_edit_requests_all',
        () => tryRequestCandidates([() => trackPromise(apiClient.get('/api/edit-requests/list'))]),
        (response) => {
            const data = response?.data?.data ?? response?.data;
            return asArray(data);
        }
    );

    return rows.map(normalizePendingEditRequest).filter((item) => item.id !== undefined && item.id !== null);
}

async function listPendingEditRequestsByUser() {
    // const response = await tryRequestCandidates([() => trackPromise(apiClient.get('/api/edit-requests/pending/me'))]);
    //
    // const rows = asArray(response?.data?.data ?? response?.data);
    // return rows.map(normalizePendingEditRequest).filter((item) => item.id !== undefined && item.id !== null);
    const rows = await offlineGet(
        'pending_edit_requests_me',
        () => tryRequestCandidates([() => trackPromise(apiClient.get('/api/edit-requests/list/me'))]),
        (response) => {
            const data = response?.data?.data ?? response?.data;
            return asArray(data);
        }
    );

    const onlineItems = rows.map(normalizePendingEditRequest).filter((item) => item.id !== undefined && item.id !== null);

    // 🔥 agregar offline
    const offlineItems = getQueue()
        .filter((item) => item.type === 'CREATE_EDIT_REQUEST')
        .map((item) => ({
            ...item.payload,
            id: item.id, // temporal
            offline: true
        }));

    return [...offlineItems, ...onlineItems];
}

async function resolveEditRequest({ requestId, approved, rejectionReason }) {
    const normalizedId = String(requestId);
    const reason = rejectionReason?.trim() || undefined;
    const status = approved ? 'APPROVED' : 'REJECTED';
    const action = approved ? 'APPROVE' : 'REJECT';
    const endpoints = [
        `/api/edit-requests/hourly/edit-request/${normalizedId}`,
        `/api/productionEditRequest/hourly/edit-request/${normalizedId}`
    ];
    const payloadCandidates = [
        { approved, rejectionReason: reason, reason, comment: reason },
        { status, reason, comment: reason },
        { action, reason, comment: reason }
    ];

    let lastError;

    for (const endpoint of endpoints) {
        let shouldTryNextEndpoint = false;

        for (const payload of payloadCandidates) {
            try {
                return await apiClient.post(endpoint, payload);
            } catch (error) {
                const statusCode = error?.response?.status;
                lastError = error;

                if (statusCode === 404 || statusCode === 405) {
                    shouldTryNextEndpoint = true;
                    break;
                }

                if (statusCode === 400 || statusCode === 422) {
                    continue;
                }

                throw error;
            }
        }

        if (!shouldTryNextEndpoint && lastError) {
            throw lastError;
        }
    }

    throw lastError || new Error('No se encontró un endpoint válido para atender la solicitud de edición.');
}

const editRequestService = {
    listEditRequests: listMyProductionRecords,
    createEditRequest,
    listPendingEditRequests,
    resolveEditRequest,
    listPendingEditRequestsByUser
};

export default editRequestService;
